/**
 * ReadonlyLabeledEdge — the edge used ONLY by the read-only graph preview
 * (PipelineGraphView). It draws the same smoothstep path as the editor's
 * default edges, but takes over label placement so branch chips don't sit on
 * top of the node a "skip" edge routes past.
 *
 * Just the "B" half of the fix: keep React Flow's default label POSITION (the
 * path midpoint — source-anchoring was tried and rejected: two edges off the
 * same fork stacked their labels on top of each other, worse than the midpoint
 * spread), but render the label as an opaque chip raised above node bodies (in
 * the CSS) so wherever it lands it stays legible instead of melting into the
 * icon/title behind it.
 *
 * Scoped to readonly: GraphFlowEditor only swaps labeled edges to this type
 * when `readonly` is set, so the editor's interactive "?" conditional edge is
 * untouched.
 */

import React from 'react';
import { EdgeLabelRenderer, BaseEdge, getSmoothStepPath } from 'reactflow';
import './ReadonlyLabeledEdge.css';

export const ReadonlyLabeledEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  label,
}) => {
  // Same path geometry as the editor's default 'smoothstep' edges so the line
  // itself looks identical. labelX/labelY are React Flow's own midpoint — the
  // original ("old") label position the user preferred; we keep it and only
  // restyle the chip.
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    borderRadius: 8,
  });

  return (
    <>
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} style={style} />
      {label && (
        <EdgeLabelRenderer>
          <div
            className="readonly-edge-label"
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            }}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

export const ReadonlyLabeledEdgeType = ReadonlyLabeledEdge;
export default ReadonlyLabeledEdge;
